'use client'

import Link from 'next/link'
import * as React from 'react'

const STORAGE_KEY = 'gwt-cookie-consent'

export type CookieConsentCopy = {
  banner: string
  acceptLabel: string
  essentialOnlyLabel: string
  cookiePolicyLabel: string
}

type Props = {
  locale: string
  copies: CookieConsentCopy
}

export function CookieConsentBar({ locale, copies }: Props) {
  const [mounted, setMounted] = React.useState(false)
  const [hidden, setHidden] = React.useState(false)

  React.useEffect(() => {
    setMounted(true)
    try {
      if (typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY)) {
        setHidden(true)
      }
    } catch {
      setHidden(false)
    }
  }, [])

  function persist(choice: 'all' | 'essential') {
    try {
      localStorage.setItem(STORAGE_KEY, choice)
    } catch {
      /* ignore */
    }
    setHidden(true)
  }

  if (!mounted || hidden) return null

  const cookieHref = `/${locale}/legal/cookies`

  return (
    <div data-component-id="src/components/legal/CookieConsentBar.tsx:49:4" data-component-path="src/components/legal/CookieConsentBar.tsx" data-component-line="49" data-component-end-line="79" data-component-file="CookieConsentBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-x-0%20bottom-0%20z-%5B110%5D%20flex%20max-h-%5B50vh%5D%20flex-col%20gap-3%20border-t%20border-slate-700%2F90%20bg-%5B%23141416%5D%20px-4%20py-4%20text-slate-100%20shadow-%5B0_-12px_40px_rgba(0%2C0%2C0%2C0.35)%5D%20md%3Aflex-row%20md%3Aitems-center%20md%3Ajustify-between%20md%3Apx-8%20md%3Apy-5%22%7D"
      className="fixed inset-x-0 bottom-0 z-[110] flex max-h-[50vh] flex-col gap-3 border-t border-slate-700/90 bg-[#141416] px-4 py-4 text-slate-100 shadow-[0_-12px_40px_rgba(0,0,0,0.35)] md:flex-row md:items-center md:justify-between md:px-8 md:py-5"
      role="dialog"
      aria-label="Cookie preferences"
      aria-live="polite"
    >
      <p data-component-id="src/components/legal/CookieConsentBar.tsx:55:6" data-component-path="src/components/legal/CookieConsentBar.tsx" data-component-line="55" data-component-end-line="55" data-component-file="CookieConsentBar.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22max-w-3xl%20text-sm%20leading-relaxed%20text-slate-300%20md%3Aflex-1%22%7D" className="max-w-3xl text-sm leading-relaxed text-slate-300 md:flex-1">{copies.banner}</p>
      <div data-component-id="src/components/legal/CookieConsentBar.tsx:56:6" data-component-path="src/components/legal/CookieConsentBar.tsx" data-component-line="56" data-component-end-line="78" data-component-file="CookieConsentBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20shrink-0%20flex-wrap%20items-center%20gap-2%20md%3Ajustify-end%20md%3Agap-3%22%7D" className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end md:gap-3">
        <Link data-component-id="src/components/legal/CookieConsentBar.tsx:57:8" data-component-path="src/components/legal/CookieConsentBar.tsx" data-component-line="57" data-component-end-line="63" data-component-file="CookieConsentBar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%5Bvar%3AcookieHref%5D%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-blue-400%20underline%20underline-offset-2%20hover%3Atext-blue-300%22%7D"
          href={cookieHref}
          className="text-sm font-semibold text-blue-400 underline underline-offset-2 hover:text-blue-300"
          prefetch={false}
        >
          {copies.cookiePolicyLabel}
        </Link>
        <button data-component-id="src/components/legal/CookieConsentBar.tsx:64:8" data-component-path="src/components/legal/CookieConsentBar.tsx" data-component-line="64" data-component-end-line="70" data-component-file="CookieConsentBar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22rounded-full%20border%20border-slate-500%20bg-transparent%20px-4%20py-2%20text-sm%20font-semibold%20text-white%20transition%20hover%3Aborder-slate-400%20hover%3Abg-slate-800%2F80%22%7D"
          type="button"
          onClick={() => persist('essential')}
          className="rounded-full border border-slate-500 bg-transparent px-4 py-2 text-sm font-semibold text-white transition hover:border-slate-400 hover:bg-slate-800/80"
        >
          {copies.essentialOnlyLabel}
        </button>
        <button data-component-id="src/components/legal/CookieConsentBar.tsx:71:8" data-component-path="src/components/legal/CookieConsentBar.tsx" data-component-line="71" data-component-end-line="77" data-component-file="CookieConsentBar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22rounded-full%20bg-white%20px-4%20py-2%20text-sm%20font-semibold%20text-%5B%23141416%5D%20transition%20hover%3Abg-slate-100%22%7D"
          type="button"
          onClick={() => persist('all')}
          className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#141416] transition hover:bg-slate-100"
        >
          {copies.acceptLabel}
        </button>
      </div>
    </div>
  )
}
